import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { fetchDemonstrationClips } from "../../graphqlHasura/features/podrankDemo/fetch_specific_podcasts_recent_episode_clips";
import {
  Fetch_Demonstration_ClipsQuery,
  Fetch_Demonstration_ClipsQueryVariables,
  Clip_Client_Projectable_DataFragment,
  Person_Entity_Client_ProjectionFragment,
} from "../../generated/hasuraGraphql";
import { PodcastSelectionInterface } from "./PodcastSelectionInterface";
import { ClientClipProjection } from "./ViewModels/ClientClipProjection";
import { ClipPersonEntity } from "./ViewModels/ClipPersonEntity";
import { standardClipThumbnailImageUrl } from "./cloudinaryClipThumbnailImageGen";
import { flatMapOptional } from "../../Infrastucture/MissingStd";
import {
  DurationKind,
  maximumDurationForDurationKind,
  minimumDurationForDurationKind,
} from "./DurationKind";

function clientProjectionThumbnailImageUrl(
  projectableData: Clip_Client_Projectable_DataFragment
): string {
  const personEntityToRandomPicture: (
    personEntity: Person_Entity_Client_ProjectionFragment
  ) => string | null = (personEntity) => {
    const allPersonPictures = personEntity.person_entity_pictures;
    try {
      return allPersonPictures[
        Math.floor(Math.random() * allPersonPictures.length)
      ].cloudinary_public_id;
    } catch (error) {
      return null;
    }
  };

  const nonHostPersonEntities = (
    projectableData.podcast_episode?.non_host_person_entities || []
  ).map((nonHostPersonEntity) => nonHostPersonEntity.person_entity);
  const hostPersonEntities = (
    projectableData.podcast_episode?.host_person_entities || []
  ).map((hostPersonEntity) => hostPersonEntity.person_entity);

  const allPersonEntitiesInOrderOfPreference = [
    ...nonHostPersonEntities,
    ...hostPersonEntities,
  ];

  const person1 = allPersonEntitiesInOrderOfPreference[0];
  // const person2 =
  //   projectableData.podcast_episode?.person_entity_episodes[1].person_entity ||
  //   person1;

  const podcastImage =
    projectableData?.podcast_episode?.podcast_channel?.image_cloudinary_id;
  const possibleBorderColors = [
    "white",
    "red",
    "orange",
    "yellow",
    "green",
    "blue",
    "indigo",
    "violet",
  ];
  const borderColor =
    possibleBorderColors[
      Math.floor(Math.random() * possibleBorderColors.length)
    ];

  const podcastPicture =
    projectableData.podcast_clip_pictures[
      Math.floor(Math.random() * projectableData.podcast_clip_pictures.length)
    ];

  return standardClipThumbnailImageUrl(
    flatMapOptional(person1, personEntityToRandomPicture) || "",
    podcastImage || "",
    podcastPicture.cloudinary_public_id,
    borderColor
  );
}

function clipFragmentToPersonEntities(
  clipFragment: Clip_Client_Projectable_DataFragment
): ClipPersonEntity[] {
  const personEntityToClipPersonEntity: (
    personEntity: Person_Entity_Client_ProjectionFragment
  ) => ClipPersonEntity = (personEntity) =>
    new ClipPersonEntity(
      personEntity.name,
      personEntity.description || "",
      personEntity.twitter_handle || undefined
    );

  const nonHostPersonEntities = (
    clipFragment.podcast_episode?.non_host_person_entities || []
  ).map((nonHostPersonEntity) => nonHostPersonEntity.person_entity);

  return nonHostPersonEntities.map(personEntityToClipPersonEntity);

  return [];
}

function clientProjectableToClientProjection(
  projectableData: Clip_Client_Projectable_DataFragment
): ClientClipProjection | null {
  try {
    return new ClientClipProjection(
      projectableData.podcast_episode.audio_url || "",
      (projectableData.start_time as number | undefined) || 0,
      (projectableData.end_time as number | undefined) || 0,
      (projectableData.duration as number | undefined) || 0,
      projectableData.description || "",
      clientProjectionThumbnailImageUrl(projectableData),
      clipFragmentToPersonEntities(projectableData)
    );
  } catch (error) {
    return null;
  }
}

export async function fetchClipsData(
  selectedPodcasts: PodcastSelectionInterface[],
  selectedDurationKind: DurationKind,
  apolloClient: ApolloClient<NormalizedCacheObject>
): Promise<ClientClipProjection[]> {
  /* eslint-disable  @typescript-eslint/no-unsafe-assignment */

  const allowedPodcastChannelIds = `{${selectedPodcasts
    .map((podcast) => podcast.channelId)
    .join(",")}}`;

  const { error, data } = await apolloClient.query<
    Fetch_Demonstration_ClipsQuery,
    Fetch_Demonstration_ClipsQueryVariables
  >({
    query: fetchDemonstrationClips,
    variables: {
      allowed_podcast_channel_ids: allowedPodcastChannelIds,
      minimum_duration: minimumDurationForDurationKind(selectedDurationKind),
      maximum_duration: maximumDurationForDurationKind(selectedDurationKind),
    },
    fetchPolicy: "no-cache",
  });

  if (error || !data) {
    throw (
      error ||
      new Error("No explicit error, but also no data returned from query")
    );
  }

  return data.get_clips_of_recent_episodes
    .tawkitExtensions()
    .compactMap(clientProjectableToClientProjection);
}
